import { useTranslation } from "react-i18next";
import { useState } from "react";
import type { TenantBootstrap } from "../tenant/bootstrap.js";
import type { AuthSession } from "../auth/keycloak.js";
import { useMe } from "../api/useMe.js";
import { AppShell } from "../components/AppShell.js";
import { Button } from "@/components/ui/button";
import { AppHeader, BrandMark } from "../components/header/AppHeader.js";
import { OnlineUsers } from "../components/header/OnlineUsers.js";
import { BackgroundJobs } from "../components/header/BackgroundJobs.js";
import { useMediaJobs } from "../realtime/useMediaJobs.js";
import { usePresence } from "../realtime/usePresence.js";
import { AppFooter } from "../components/AppFooter.js";
import { ThemeToggle } from "../components/header/ThemeToggle.js";
import { LanguageMenu } from "../components/header/LanguageMenu.js";
import { logout } from "../auth/keycloak.js";
import { AccountPanel } from "../components/header/AccountPanel.js";
import { displayInitials } from "../components/header/display-initial.js";
// FN (ADR-014): the facility navigator is per-tenant — the classic master-detail (`classic/`) or the drill-down
// variant, selected client-side by `session.realm` via `facilityNavFor`.
import { facilityNavFor } from "@arc/tenant-config";
import { FacilitySelection } from "../features/facilities/classic/FacilitySelection.js";
import { FacilityDrilldown } from "../features/facilities/drilldown/FacilityDrilldown.js";
// ADR-032: the "Dashboard & settings" workspace is a top-level route ABOVE the realm split (both navigators get it).
import { useFacilityNavParams } from "../features/facilities/drilldown/useFacilityNavParams.js";
import { WorkspaceView } from "../features/workspace/WorkspaceView.js";
import { DEFAULT_WORKSPACE_VIEW } from "../features/workspace/workspace-pages.js";
import { WorkspaceButton } from "../components/header/WorkspaceButton.js";
// ADR-036: a session that is ONLY a shop-floor kiosk gets the chrome-less kiosk landing instead of the app.
import { useFacilities } from "../features/facilities/useFacilities.js";
import { isKioskOnlySession, KIOSK_ROLE } from "../features/kiosk/kiosk-session.js";
import { isKioskSuspended, resumeKiosk, suspendKiosk } from "../features/kiosk/kiosk-device.js";
import { KioskView } from "../features/kiosk/KioskView.js";
// ADR-055: the project-management board — `/pm`, one pathname switch.
import { isPmBoardPath } from "../features/pm-board/pm-nav.js";
import { PmBoard } from "../features/pm-board/PmBoard.js";
// The task board: `/task-board`, one pathname switch, SuperAdmin only.
import { TaskBoard, isTaskBoardPath } from "../features/task-board/TaskBoard.js";

/**
 * Shell only — deliberately no component library and no hand-rolled widgets yet (that is what guarantees rework).
 * Native semantic HTML + the token layer; the design system drops in later behind the same CSS custom properties.
 */
export function App({
  tenant,
  session = null,
  authError,
}: {
  tenant: TenantBootstrap | null;
  session?: AuthSession | null;
  authError?: string;
}) {
  const { t } = useTranslation();
  // Hooks must run unconditionally, so this sits above the early return. `enabled` keeps it inert until there is
  // a session, and the unknown-tenant path below never has one.
  const me = useMe(session);
  // Realm-wide "who's online", pushed over `/cable` (ADR-026 Phase 5). Empty until the socket connects, and the header
  // renders nothing in that case rather than asserting "0 online".
  const online = usePresence(session);
  // The caller's own background work (ADR-026 Phase 6) — a finished cut arrives over the socket, so no reload is needed.
  const { jobs, isLoading: jobsLoading, isError: jobsError, retry: retryJobs } = useMediaJobs(session);
  // The URL state, read here for ONE param (`arc_view`) + the editor guard; the navigators read the rest themselves.
  const nav = useFacilityNavParams();
  // The route-level guard (ADR-032 plan panel): a URL carrying BOTH `arc_view` and `arc_edit` never unmounts the editor.
  const workspaceOpen = nav.view !== null && nav.editManualId === null;
  // Kiosk detection (ADR-036 D3) — the `GenericUser` ROLE *and* no capability above `view`, so a person who merely
  // also holds the role (a composite role, an admin testing the device) is never trapped in a chrome-less screen.
  // Shares the navigator's own `["facilities", …]` query, so this costs no extra request.
  const facilities = useFacilities(session);
  const [kioskSuspended, setKioskSuspended] = useState(isKioskSuspended);
  // The role alone, known as soon as `/me` lands. The CAPABILITY half needs the tree, so the two are separate: an
  // empty `facilities.data` mid-fetch would read as "no capability" and flash the kiosk at an admin who holds the role.
  const kioskRole = me.data ? me.data.roles.includes(KIOSK_ROLE) : false;
  const kioskOnly = me.data ? isKioskOnlySession(me.data.roles, facilities.data ?? []) : false;
  const kioskActive = kioskOnly && !kioskSuspended && facilities.data !== undefined;
  /**
   * A kiosk-only account must NEVER be shown the authoring navigator, not even for the frame between `/me` and
   * `/facilities` landing (diff panel, Medium). `/me` usually resolves first, and on a wall-mounted screen that frame
   * is a visible flash of the full authoring UI — and a terminal `/facilities` failure would leave it there for good.
   * So while a role-holder's tree is still unknown, the shell holds a neutral line instead of picking a navigator.
   */
  const kioskUndecided = kioskRole && !kioskSuspended && facilities.data === undefined;
  // D1: `/pm` mounts the board instead of the workspace — but only with access; without it `/pm` is the normal app (the
  // static edge serves the tenant's index.html for any path). Assumed until `/me` lands, so a member sees no flash of the
  // workspace; the board's own load is what a person without access sees for that moment. A kiosk keeps its own screen.
  const pmBoard = session !== null && isPmBoardPath(window.location.pathname) && (me.data?.pmAccess ?? true) && !kioskActive && !kioskUndecided;
  // A signed-in user without SuperAdmin gets the normal app; the API answers 403 anyway.
  const taskBoard = session !== null && isTaskBoardPath(window.location.pathname) && me.data?.isSuper === true && !kioskActive && !kioskUndecided;

  // The unknown-host path stays OUTSIDE AppShell on purpose: the shell's brand and footer read tenant fields, so
  // wrapping a null tenant would dereference null and replace this deliberate fail-closed diagnostic with a crash.
  if (!tenant) {
    return (
      <main id="main">
        <h1>{t("app.unknownTenant")}</h1>
        <p>
          {/* ONE key, not two: adjacent JSX expression containers drop the whitespace between them (plain JSX
              text collapses it to a space, an expression boundary does not), so a split sentence rendered
              "…allow-listed andfails closed by design." in both languages — and a sentence is the unit a
              translator can reorder anyway. */}
          {t("app.unknownTenantBody")}
        </p>
      </main>
    );
  }

  return (
    <AppShell
      fillViewport={pmBoard}
      // the workspace shell, the kiosk and the drilldown navigator all fill main down to the footer (the drilldown's
      // muted list band would otherwise stop under a short level and leave the page background showing below it)
      mainClassName={
        pmBoard || nav.view !== null || kioskActive || kioskUndecided || (session && facilityNavFor(session.realm) === "drilldown")
          ? "flex flex-col"
          : undefined
      }
      header={
        <AppHeader
          brand={<BrandMark productName={tenant.productName} realm={tenant.realm} clientName={tenant.clientName} />}
          actions={
            <>
              {/* Nothing until /me resolves: a chip that flashes a placeholder and then corrects itself is
                  worse than one that appears a moment later. */}
              {me.data ? (
                <AccountPanel
                  displayName={me.data.displayName}
                  initial={displayInitials(me.data.displayName)}
                  roles={me.data.roles}
                  // Wired: local realms now declare exact post-logout URIs (commit 5fcab69), so keycloak-js
                  // no longer gets its redirect rejected. STAGING AND PRODUCTION ARE STILL UNREGISTERED — the
                  // operator runbook is in rewrite-docs/hosting-dns.md, and this control will fail there until
                  // it is done. `void`: logout navigates away, so nothing awaits its promise.
                  onSignOut={() => void logout()}
                />
              ) : null}
              {/* Authoring chrome, hidden on a kiosk: a shared shop-floor account has no background cuts of its own
                  and "who's online" is noise on a wall-mounted screen (ADR-036 — the legacy hid these with CSS). */}
              {kioskActive || kioskUndecided ? null : (
                <>
                  <BackgroundJobs jobs={jobs} isLoading={jobsLoading} isError={jobsError} onRetry={retryJobs} />
                  <OnlineUsers users={online} selfId={session?.sub ?? null} />
                </>
              )}
              {kioskOnly && kioskSuspended ? (
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => {
                    resumeKiosk();
                    setKioskSuspended(false);
                  }}
                >
                  {t("app.returnToKioskView")}
                </Button>
              ) : null}
              <LanguageMenu defaultLocale={tenant?.defaultLocale} realm={tenant?.realm} />
              <ThemeToggle />
              {/* Divider before the menu group, per the target layout. Decorative, so aria-hidden. The group holds the
                  legacy hamburger — the "Dashboard & settings" workspace toggle (ADR-032); notification counts still
                  wait for a model that does not exist. */}
              <span aria-hidden="true" className="bg-border mx-1 h-5 w-px" />
              {session && me.data && !kioskActive && !kioskUndecided && !pmBoard && !taskBoard ? (
                <WorkspaceButton
                  isOpen={workspaceOpen}
                  isDisabled={nav.editManualId !== null}
                  onPress={() => (nav.view !== null ? nav.closeView() : nav.openView(DEFAULT_WORKSPACE_VIEW))}
                />
              ) : null}
            </>
          }
        />
      }
      footer={<AppFooter clientName={tenant.clientName} version={__APP_VERSION__} />}
    >
      {/* Authenticated + profile loaded ⇒ the primary screen (facility selection), which owns the page <h1>. The
          sr-only product name is only needed as a heading anchor during the transient/error states below (a document
          with no h1 breaks screen-reader heading navigation). */}
      {session && me.data ? (
        kioskUndecided ? (
          // Neither navigator yet — see `kioskUndecided`. A role-holder whose tree FAILED stays here with a retry
          // rather than dropping onto the authoring screen this account was never meant to see.
          <div className="flex min-h-0 flex-1 flex-col items-start gap-3 p-6">
            <h1 className="sr-only">{tenant.productName}</h1>
            {facilities.isError ? (
              <>
                <p role="alert" className="text-destructive text-sm">
                  {t("app.couldntReachTheServerTheDevice")}
                </p>
                <Button variant="outline" size="sm" onPress={() => void facilities.refetch()} isDisabled={facilities.isFetching}>
                  {t("app.tryAgain")}
                </Button>
              </>
            ) : (
              <p role="status" className="text-muted-foreground text-sm">
                {t("app.loadingThisDevicesKiosk")}
              </p>
            )}
          </div>
        ) : kioskActive ? (
          // The kiosk wins over every other view — it IS the device's whole app (ADR-036 Phase 3). "Leave kiosk view"
          // is the escape hatch D3 guard 2 requires, so no one ever needs Keycloak access to get out.
          <KioskView
            session={session}
            onLeave={() => {
              suspendKiosk();
              setKioskSuspended(true);
            }}
          />
        ) : pmBoard ? (
          <PmBoard session={session} meId={me.data.id} />
        ) : taskBoard ? (
          <TaskBoard session={session} />
        ) : workspaceOpen ? (
          // The workspace wins over every other view (legacy parity); the navigator never mounts underneath it, so a
          // deep link lands straight on the page with no list flash.
          <WorkspaceView session={session} me={me.data} tenant={tenant} view={nav.view!} />
        ) : facilityNavFor(session.realm) === "drilldown" ? (
          <FacilityDrilldown session={session} />
        ) : (
          <FacilitySelection session={session} />
        )
      ) : (
        <>
          <h1 className="sr-only">{tenant.productName}</h1>
          {authError ? (
            <p role="alert" className="p-4">
              {t("app.signInFailed", { reason: authError })}
            </p>
          ) : me.error ? (
            <p role="alert" className="p-4">
              {t("app.couldNotLoadProfile", { reason: me.error.message })}
            </p>
          ) : !session ? (
            <p className="text-muted-foreground p-4">{t("app.signingIn")}</p>
          ) : (
            <p role="status" className="text-muted-foreground p-4">
              {t("app.loadingYourProfile")}
            </p>
          )}
        </>
      )}
    </AppShell>
  );
}
